import { inject, Pipe, PipeTransform } from '@angular/core';

import { Observable, of } from 'rxjs';

import { FileDownloadService } from '../services/file/file-download.base-service';

/**
 * The part of a content entity (news, offer) that is needed to resolve its title image.
 */
export interface IContentTitleImageSource {
  id: string;
  titleImageId?: string | null;
  // Only set by the V1 public content queries, which still return a plain url.
  imageUrl?: string;
}

/**
 * Whether a content entity has a title image. This is known synchronously, before the image itself
 * has been downloaded, so the layout can reserve space for it and avoid a shift once it arrives.
 */
export function hasContentTitleImage(
  content: IContentTitleImageSource | null | undefined
): boolean {
  return !!content?.imageUrl || content?.titleImageId != null;
}

@Pipe({
  name: 'hasTitleImage',
  standalone: true,
})
export class HasTitleImagePipe implements PipeTransform {
  transform(content: IContentTitleImageSource | null | undefined): boolean {
    return hasContentTitleImage(content);
  }
}

/**
 * Resolves the title image of a content entity to a source that can be bound to an image.
 *
 * Content V2 returns a title image id instead of a url, and the file has to be fetched from
 * filer-rs with an authorization header, so the source can only be produced asynchronously. Guard
 * the surrounding element with `hasTitleImage` so the space is reserved before it resolves.
 */
@Pipe({
  name: 'contentTitleImage',
  standalone: true,
})
export class ContentTitleImagePipe implements PipeTransform {
  #fileDownloadService = inject(FileDownloadService);

  transform(content: IContentTitleImageSource | null | undefined): Observable<string> {
    if (content?.imageUrl) {
      return of(content.imageUrl);
    }

    if (!content?.id || content.titleImageId == null) {
      return of(null);
    }

    return this.#fileDownloadService.getContentTitleImage(content.id, content.titleImageId);
  }
}
